/**
 * Featured Quizzes
 *
 * Curated selection of ready-to-play quizzes for quick start.
 * These are pre-configured with sensible defaults for beginners.
 */

import type { Category } from "@/db/types";

export interface FeaturedQuizConfig {
  id: string;
  title: string;
  description: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  category: Category;
  datasetPath: string;
  datasetLabel: string;
  promptKey: string;
  estimatedTime: string; // e.g., "5 min"
  featureCount: number;
}

export const featuredQuizzes: FeaturedQuizConfig[] = [
  // Top Featured Quizzes
  {
    id: "usa-area-codes",
    title: "US Area Codes",
    description: "Master the 3-digit area codes across all US states",
    difficulty: "advanced",
    category: "phone",
    datasetPath: "/geojson/phones/US_3.json",
    datasetLabel: "United States · Phone Area Codes (3 digits)",
    promptKey: "AreaCode",
    estimatedTime: "15 min",
    featureCount: 348,
  },
  {
    id: "brazil-area-codes",
    title: "Brazilian Area Codes",
    description: "Learn Brazil's 2-digit phone area codes across all states",
    difficulty: "intermediate",
    category: "phone",
    datasetPath: "/geojson/phones/BR_2.json",
    datasetLabel: "Brazil · Phone Area Codes (2 digits)",
    promptKey: "AreaCode",
    estimatedTime: "8 min",
    featureCount: 67,
  },
  {
    id: "vietnam-provinces",
    title: "Vietnamese Provinces",
    description: "Learn all 64 provinces and cities of Vietnam",
    difficulty: "intermediate",
    category: "admin",
    datasetPath: "/geojson/regions/adm/VNM_ADM1.json",
    datasetLabel: "Vietnam · Provinces (ADM1)",
    promptKey: "Name",
    estimatedTime: "10 min",
    featureCount: 64,
  },
  {
    id: "indonesia-regencies",
    title: "Indonesian Regencies",
    description: "Master all 519 kabupaten and kota across Indonesia's provinces",
    difficulty: "advanced",
    category: "admin",
    datasetPath: "/geojson/regions/adm/IDN_ADM2.json",
    datasetLabel: "Indonesia · Regencies (ADM2)",
    promptKey: "Name",
    estimatedTime: "20 min",
    featureCount: 519,
  },
  {
    id: "germany-districts",
    title: "German Districts",
    description: "Learn Germany's 428 Landkreise across all 16 states",
    difficulty: "advanced",
    category: "admin",
    datasetPath: "/geojson/regions/adm/DEU_ADM3.json",
    datasetLabel: "Germany · Districts (ADM3)",
    promptKey: "Name",
    estimatedTime: "18 min",
    featureCount: 428,
  },
    {
        id: "germany-states",
        title: "German States",
        description: "Learn the 16 federal states (Bundesländer) of Germany",
        difficulty: "beginner",
        category: "admin",
        datasetPath: "/geojson/regions/adm/DEU_ADM1.json",
        datasetLabel: "Germany · States (ADM1)",
        promptKey: "Name",
        estimatedTime: "3 min",
        featureCount: 16,
    },

  // Other Popular Quizzes
  {
    id: "usa-states",
    title: "US States",
    description: "Learn all 50 United States by their shapes and locations",
    difficulty: "beginner",
    category: "admin",
    datasetPath: "/geojson/regions/adm/USA_ADM1.json",
    datasetLabel: "United States · States (ADM1)",
    promptKey: "Name",
    estimatedTime: "5 min",
    featureCount: 51,
  },

  // Beginner - Europe
  {
    id: "france-departments",
    title: "French Départements",
    description: "Learn France's 96 départements and their locations",
    difficulty: "intermediate",
    category: "admin",
    datasetPath: "/geojson/regions/adm/FRA_ADM2.json",
    datasetLabel: "France · Départements (ADM2)",
    promptKey: "Name",
    estimatedTime: "10 min",
    featureCount: 96,
  },
  {
    id: "uk-area-codes",
    title: "UK Area Codes",
    description: "Master the 2-5 digit UK phone area codes by region",
    difficulty: "advanced",
    category: "phone",
    datasetPath: "/geojson/phones/UK_2.json",
    datasetLabel: "United Kingdom · Phone Area Codes (2-5 digits)",
    promptKey: "AreaCode",
    estimatedTime: "20 min",
    featureCount: 642,
  },

  // Beginner - Asia
  {
    id: "japan-prefectures",
    title: "Japanese Prefectures",
    description: "Learn all 47 prefectures of Japan",
    difficulty: "intermediate",
    category: "admin",
    datasetPath: "/geojson/regions/adm/JPN_ADM1.json",
    datasetLabel: "Japan · Prefectures (ADM1)",
    promptKey: "Name",
    estimatedTime: "8 min",
    featureCount: 47,
  },
  {
    id: "philippines-provinces",
    title: "Philippine Provinces",
    description: "Learn the provinces of the Philippines across its 17 regions",
    difficulty: "advanced",
    category: "admin",
    datasetPath: "/geojson/regions/adm/PHL_ADM2.json",
    datasetLabel: "Philippines · Provinces (ADM2)",
    promptKey: "Province",
    estimatedTime: "12 min",
    featureCount: 85,
  },

  // Beginner - Americas
  {
    id: "canada-provinces",
    title: "Canadian Provinces & Territories",
    description: "Learn Canada's 10 provinces and 3 territories",
    difficulty: "beginner",
    category: "admin",
    datasetPath: "/geojson/regions/adm/CAN_ADM1.json",
    datasetLabel: "Canada · Provinces and Territories (ADM1)",
    promptKey: "Name",
    estimatedTime: "3 min",
    featureCount: 13,
  },
  {
    id: "brazil-states",
    title: "Brazilian States",
    description: "Learn the 26 states and Federal District of Brazil",
    difficulty: "intermediate",
    category: "admin",
    datasetPath: "/geojson/regions/adm/BRA_ADM1.json",
    datasetLabel: "Brazil · States (ADM1)",
    promptKey: "Name",
    estimatedTime: "5 min",
    featureCount: 27,
  },

  // Phone codes - Popular
  {
    id: "france-area-codes",
    title: "French Area Codes",
    description: "Master France's 4-digit area codes for départements",
    difficulty: "intermediate",
    category: "phone",
    datasetPath: "/geojson/phones/FR_3.json",
    datasetLabel: "France · Phone Area Codes (4 digits)",
    promptKey: "AreaCode",
    estimatedTime: "10 min",
    featureCount: 72,
  },
  {
    id: "spain-provinces",
    title: "Spanish Provinces",
    description: "Learn Spain's 50 provinces across its autonomous communities",
    difficulty: "intermediate",
    category: "admin",
    datasetPath: "/geojson/regions/adm/ESP_ADM2.json",
    datasetLabel: "Spain · Provinces (ADM2)",
    promptKey: "Name",
    estimatedTime: "8 min",
    featureCount: 50,
  },
  {
    id: "italy-regions",
    title: "Italian Regions",
    description: "Learn Italy's 20 regions from north to south",
    difficulty: "beginner",
    category: "admin",
    datasetPath: "/geojson/regions/adm/ITA_ADM1.json",
    datasetLabel: "Italy · Regions (ADM1)",
    promptKey: "Name",
    estimatedTime: "4 min",
    featureCount: 20,
  },
];

/**
 * Get featured quizzes by difficulty
 */
export function getFeaturedQuizzesByDifficulty(
  difficulty: "beginner" | "intermediate" | "advanced"
): FeaturedQuizConfig[] {
  return featuredQuizzes.filter((q) => q.difficulty === difficulty);
}

/**
 * Get featured quizzes by category
 */
export function getFeaturedQuizzesByCategory(
  category: Category
): FeaturedQuizConfig[] {
  return featuredQuizzes.filter((q) => q.category === category);
}

/**
 * Get top N featured quizzes (for homepage)
 */
export function getTopFeaturedQuizzes(limit = 6): FeaturedQuizConfig[] {
  return featuredQuizzes.slice(0, limit);
}
